"use client";

import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { staggerContainerVariants, staggerItemVariants } from "@/lib/animations";

const APP_STORE_URL = "https://apps.apple.com/us/app/nombox-grocery-list/id6755436826";

const IPHONE_SCREENSHOTS = {
  categorizedList:
    "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource211/v4/7a/81/37/7a81374a-ed67-88dc-6850-070e1780137d/Simulator_Screenshot_-_iPhone_17_Pro_Max_-_2025-11-29_at_04.55.00.png/460x998bb.webp",
  naturalInput:
    "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource211/v4/7b/84/12/7b841295-56b9-bac1-c7d2-47484888aa68/Simulator_Screenshot_-_iPhone_17_Pro_Max_-_2025-11-29_at_04.51.25.png/460x998bb.webp",
};

const WATCH_SCREENSHOTS = [
  "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/2e/ba/83/2eba831d-6154-b5c3-26e9-82483dd166b3/Simulator_Screenshot_-_Apple_Watch_Series_11__U002846mm_U0029_-_2025-11-24_at_04.15.06.png/400x400bb.webp",
  "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/7c/73/19/7c731903-1c3a-ed2e-02c4-2242827a2e8b/Simulator_Screenshot_-_Apple_Watch_Series_11__U002846mm_U0029_-_2025-11-24_at_04.15.23.png/400x400bb.webp",
];

const BG_ASPECT_RATIOS = ["4/5", "1/1", "4/3", "3/4", "4/5", "1/1", "3/4", "4/5", "4/3"];

// Screen offsets as percentages of frame dimensions (Apple Frames 4.0)
// iPhone 16 Pro: frame 1350×2760, screen at (72, 69), screen 1206×2622
const IPHONE_SCREEN = { left: "5.1%", top: "2.3%", width: "89.8%", height: "95.4%" };
// Watch Series 11 42mm: frame 520×800, screen at (73, 177), screen 374×446
const WATCH_SCREEN = { left: "13.6%", top: "21.7%", width: "72.8%", height: "56.6%" };

interface WelcomeHeroProps {
  backgroundImages: string[];
}

export function WelcomeHero({ backgroundImages }: WelcomeHeroProps) {
  const t = useTranslations("welcome");

  return (
    <div className="relative flex min-h-dvh flex-col">
      {/* Masonry food photo wall (matches Discover grid) */}
      {backgroundImages.length > 0 && (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="columns-2 gap-2 opacity-50 sm:columns-3 md:columns-4 lg:columns-5"
            style={{ filter: "blur(3.5px)" }}
          >
            {Array.from(
              { length: 36 },
              (_, i) => backgroundImages[i % backgroundImages.length]
            ).map((src, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={i}
                src={src}
                alt=""
                className="mb-2 w-full break-inside-avoid rounded-lg object-cover"
                style={{ aspectRatio: BG_ASPECT_RATIOS[i % BG_ASPECT_RATIOS.length] }}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      )}

      {/* Radial vignette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, color-mix(in srgb, var(--background), transparent 48%) 0%, color-mix(in srgb, var(--background), transparent 7%) 55%, var(--background) 95%)",
        }}
      />

      {/* Content */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainerVariants(0.12)}
        className="relative z-10 flex min-h-0 flex-1 flex-col"
      >
        {/* Text + CTAs */}
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center min-h-[280px] sm:min-h-[380px]">
          <motion.h1
            variants={staggerItemVariants}
            className="text-4xl font-bold leading-[1.05] tracking-tight lg:text-5xl xl:text-6xl"
          >
            {t("headlineLine1")}
            <br />
            <span className="text-muted-foreground">{t("headlineLine2")}</span>
          </motion.h1>

          <motion.p
            variants={staggerItemVariants}
            className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            {t("subhead")}
          </motion.p>

          <motion.div
            variants={staggerItemVariants}
            className="mt-8 flex items-center gap-3 sm:gap-4"
          >
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 transition-opacity hover:opacity-80"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/badges/download-on-the-app-store.svg"
                alt={t("appStoreAlt")}
                width={150}
                height={50}
                className="w-auto h-[50px]"
              />
            </a>
            <Button variant="default" size="lg" className="shrink-0 h-[50px]" asChild>
              <Link href="/auth/signin">{t("getStarted")}</Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 8, 0] }}
            transition={{
              opacity: { delay: 1.2, duration: 0.5 },
              y: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
            }}
            className="mt-16"
          >
            <ChevronDown className="h-8 w-8 text-muted-foreground/50 md:h-10 md:w-10" />
          </motion.div>
        </div>

        {/* Device showcase */}
        <motion.div
          variants={staggerItemVariants}
          className="mt-4 flex shrink-0 items-start justify-center gap-2 px-6 sm:mt-0 sm:gap-6 md:gap-8"
        >
          {/* Left iPhone — tilted, large screens only */}
          <div className="relative hidden w-[240px] shrink-0 -rotate-5 translate-y-20 xl:block">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={IPHONE_SCREENSHOTS.naturalInput}
              alt={t("iphoneScreenshotAlt")}
              className="absolute rounded-[13%] object-cover object-top"
              style={IPHONE_SCREEN}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/devices/iphone-frame.png" alt="" className="relative z-10 h-auto w-full" />
          </div>

          {/* Center iPhone */}
          <div className="relative w-[200px] shrink-0 sm:w-[260px] md:w-[300px]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={IPHONE_SCREENSHOTS.categorizedList}
              alt={t("iphoneScreenshotAlt")}
              className="absolute rounded-[13%] object-cover object-top"
              style={IPHONE_SCREEN}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/devices/iphone-frame.png" alt="" className="relative z-10 h-auto w-full" />
          </div>

          {/* Apple Watch */}
          <div className="relative w-[100px] shrink-0 translate-y-16 sm:w-[140px] sm:translate-y-20 md:w-[180px] xl:rotate-4 xl:translate-y-30">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={WATCH_SCREENSHOTS[0]}
              alt={t("watchScreenshotAlt")}
              className="absolute rounded-[20%] object-cover"
              style={WATCH_SCREEN}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/devices/watch-frame.png" alt="" className="relative z-10 h-auto w-full" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
